import classNames from 'classnames'
import Image from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import { useMemo, useRef } from 'react'

import type { VideoPresetType, VideoSourceType } from '../../types/video'
import { ActionButton } from '../action-button'
import styles from './hero-v2.module.scss'
import { Container } from '@components/layout/grid'
import { Markdown } from '@components/markdown'
import { Text } from '@components/text'
import { Video } from '@components/video'
import type { HeroV2Fragment } from '@graphql/queries/GetHeroV2.graphql'
import { useDivDimensions } from '@lib/hooks/useDivDimensions'
import { usePersonalizations } from '@lib/hooks/usePersonalizations'
import { useWindowDimensions } from '@lib/hooks/useWindowDimensions'
import { isGradientColor } from '@lib/utils/isGradientColor'
import { renderTemplate } from '@lib/utils/text'
import variables, { type Variable } from '@styles/variables'

/**
 * Displays a content block with a title, text, image,and/or link button next to some sort of media, such as a video or image, and options for background color, image or video.
 */

export const HeroV2: FunctionComponent<Props> = ({
    layout = 'Copy Left Media Right',
    copyLayout = 'center',
    isCarousel = false,
    showWinnersBadge = false,
    headingPre = '',
    headingPost = '',
    heading = '',
    headingColor = 'white',
    headingColorMobile = 'white',
    backgroundType = 'color',
    backgroundColor = 'black',
    bgVideo,
    backgroundVideoFit = 'cover',
    backgroundVideoMobile,
    backgroundImageDesktop,
    backgroundImageMobile,
    copy,
    copyColor = 'white',
    copyColorMobile = 'white',
    supportingCopy,
    supportingCopyColor = 'white',
    copyContainerPadding = 'gapXLarge',
    button,
    secondaryButton,
    image,
    video,
    imageAlignment = 'center',
    mediaVisible = 'visible',
    mediaType = 'image',
    hideMediaOnMobile = false,
    showBackgroundBeforeContentNoMobile = false,
}) => {
    const mobileBreakpoint = 768
    const personalizations = usePersonalizations()
    const { width } = useWindowDimensions()
    const containerRef = useRef(null)
    const { divHeight } = useDivDimensions(containerRef)

    const isDesktop = width > mobileBreakpoint
    const isMobile = width < mobileBreakpoint

    const visualElement = useMemo(() => {
        if (video && mediaType === 'video') {
            return (
                <Video
                    {...video}
                    preset={(video.preset as VideoPresetType) ?? 'gif'}
                    poster={video.poster?.source}
                    alt={video.alt as string}
                    sources={video?.files?.items as VideoSourceType[]}
                    className={styles.media}
                    style={{
                        visibility:
                            mediaVisible === 'visible' ? 'visible' : 'hidden',
                    }}
                />
            )
        }

        if (image?.url && mediaType === 'image') {
            return (
                <div>
                    <Image
                        priority
                        className={styles.media}
                        src={image.url}
                        layout='responsive'
                        width={image.width}
                        height={image.height}
                        alt={image?.alt ?? ''}
                        style={{
                            visibility:
                                mediaVisible === 'visible'
                                    ? 'visible'
                                    : 'hidden',
                        }}
                    />
                </div>
            )
        }

        return null
    }, [
        mediaType,
        video,
        image?.url,
        image?.width,
        image?.height,
        image?.alt,
        mediaVisible,
    ])

    function getBackgroundStyle() {
        if (backgroundType === 'video') return
        if (backgroundType === 'image' && isDesktop) {
            return { backgroundImage: getBackgroundImageUrl() }
        }

        if (isGradientColor(backgroundColor)) {
            return { background: variables[backgroundColor as Variable] }
        }

        return { backgroundColor: variables[backgroundColor as Variable] }
    }

    function getBackgroundImageUrl() {
        let bgImageUrl = `url(${backgroundImageDesktop?.url})`

        if (isMobile && backgroundImageMobile?.url) {
            bgImageUrl = `url(${backgroundImageMobile?.url})`
        }

        return bgImageUrl
    }

    function getBgDimensions() {
        return { width: width, height: divHeight }
    }

    const renderMobileBackground = () => {
        if (!isMobile) return null

        if (backgroundVideoMobile && backgroundType === 'video') {
            return (
                <div className={styles.mobileBackgroundVideoContainer}>
                    <video
                        className={styles.bgVideo}
                        autoPlay
                        muted
                        playsInline
                        webkit-playsinline='true'
                        loop={backgroundVideoMobile?.loop}
                        poster={backgroundVideoMobile?.poster?.source}
                    >
                        <source
                            src={
                                backgroundVideoMobile?.files?.items?.[0]?.source
                            }
                            type='video/mp4'
                        />
                    </video>
                </div>
            )
        }

        if (backgroundImageMobile?.url && backgroundType === 'image') {
            return (
                <div className={styles.mobileBackgroundImageContainer}>
                    <Image
                        priority
                        className={styles.media}
                        src={backgroundImageMobile.url}
                        layout='responsive'
                        width={backgroundImageMobile.width}
                        height={backgroundImageMobile.height}
                        alt={backgroundImageMobile?.alt ?? ''}
                        style={{
                            visibility:
                                mediaVisible === 'visible'
                                    ? 'visible'
                                    : 'hidden',
                        }}
                    />
                </div>
            )
        }

        return null
    }

    return (
        <div
            className={`${styles.backgroundContainer} ${styles[backgroundVideoFit]}`}
            style={getBackgroundStyle()}
            ref={containerRef}
        >
            {isDesktop && bgVideo && backgroundType === 'video' && (
                <div
                    className={classNames(styles.videoContainer)}
                    style={getBgDimensions()}
                >
                    <video
                        className={`${styles.bgVideo} ${styles[backgroundVideoFit]}`}
                        autoPlay
                        muted
                        playsInline
                        webkit-playsinline='true'
                        loop={bgVideo.loop}
                        poster={bgVideo.poster?.source}
                    >
                        <source
                            src={bgVideo?.files?.items[0].source}
                            type='video/mp4'
                        />
                    </video>
                </div>
            )}

            {showBackgroundBeforeContentNoMobile && renderMobileBackground()}

            {/* Content Container */}
            <Container className={styles.container}>
                {showWinnersBadge && (
                    <div className={styles.winnersBadge}>
                        <Image
                            className={styles.badge}
                            src='/images/winners-badge.webp'
                            width={200}
                            height={200}
                            alt='Winners Badge'
                        />
                    </div>
                )}

                <div
                    className={classNames(styles.contentContainer, {
                        [styles.isCarousel]: isCarousel,
                        [styles.alignEnd]: imageAlignment !== 'center',
                        [styles.justifyCenter]: mediaVisible === 'center',
                        [styles.justifyLeft]: mediaVisible !== 'center',
                        [styles.rowReverse]: layout === 'Copy Right Media Left',
                    })}
                >
                    <div
                        className={classNames(
                            styles.copyContainer,
                            styles[copyContainerPadding],
                            {
                                [styles.isCarousel]: isCarousel,
                                [styles.fullWidth]:
                                    layout === 'Copy Centered No Media',
                                [styles.textCenter]:
                                    layout === 'Copy Centered No Media',
                                [styles.paddingMobile]:
                                    isMobile && hideMediaOnMobile,
                            },
                        )}
                    >
                        <div
                            className={classNames(styles[`column-inner`], {
                                [styles.centered]:
                                    layout === 'Copy Centered No Media',
                                [styles.top]: copyLayout === 'top',
                                [styles.bottom]: copyLayout === 'bottom',
                            })}
                        >
                            {/* Heading */}
                            <Text
                                typeStyle={{
                                    _: 'heading-xs',
                                    sm: 'heading-sm',
                                    md: 'heading-xl',
                                }}
                                tag='h1'
                                color={
                                    isDesktop
                                        ? headingColor
                                        : headingColorMobile
                                }
                            >
                                {headingPre ? (
                                    <span className={styles.prePostHeaderText}>
                                        {headingPre.toUpperCase()}
                                    </span>
                                ) : null}
                                {heading
                                    ?.split('<br/>')
                                    .map((text, index, array) => (
                                        <span key={index}>
                                            {text.toUpperCase()}
                                            {index < array.length - 1 && <br />}
                                        </span>
                                    ))}
                                {headingPost ? (
                                    <span className={styles.prePostHeaderText}>
                                        {headingPost.toUpperCase()}
                                    </span>
                                ) : null}
                            </Text>

                            {/* Copy */}
                            <div className={styles['copy-container']}>
                                <Markdown
                                    typeStyle={{
                                        _: 'body-lg',
                                        md: 'body-md',
                                    }}
                                    markdown={renderTemplate(
                                        copy,
                                        personalizations,
                                    )}
                                    color={
                                        isMobile ? copyColorMobile : copyColor
                                    }
                                />
                            </div>

                            <div
                                className={classNames(styles.buttons, {
                                    [styles.buttonsCentered]:
                                        layout === 'Copy Centered No Media',
                                })}
                            >
                                {!!button && (
                                    <ActionButton
                                        preset={button?.preset}
                                        {...button}
                                    />
                                )}
                                {!!secondaryButton && (
                                    <ActionButton
                                        preset={secondaryButton?.preset}
                                        {...secondaryButton}
                                    />
                                )}
                            </div>

                            {/* Supporting Copy */}
                            <div
                                className={classNames(
                                    styles.supportingCopyWrapper,
                                    {
                                        [styles.textCenter]:
                                            layout === 'Copy Centered No Media',
                                    },
                                )}
                            >
                                {supportingCopy && (
                                    <Text
                                        typeStyle={{ _: 'body-lg' }}
                                        tag='div'
                                        className={styles['supporting-copy']}
                                        color={supportingCopyColor}
                                    >
                                        {supportingCopy}
                                    </Text>
                                )}
                            </div>
                        </div>
                    </div>

                    {isMobile && hideMediaOnMobile ? null : (
                        <div
                            className={classNames(styles.imageContainer, {
                                [styles.isCarousel]: isCarousel,
                                [styles.centerAligned]:
                                    imageAlignment === 'center',
                                [styles.hidden]:
                                    layout === 'Copy Centered No Media',
                            })}
                        >
                            <div className={classNames(styles[`column-inner`])}>
                                {visualElement}
                            </div>
                        </div>
                    )}
                </div>
            </Container>

            {!showBackgroundBeforeContentNoMobile && renderMobileBackground()}
        </div>
    )
}

export default HeroV2

type Props = Omit<HeroV2Fragment, 'sys' | 'type'> & {
    index?: number
    isCarousel?: boolean
    headingPre?: string
    headingPost?: string
}
